// Shared criticality option list + display metadata for the Applications page
// (index.vue) and its create/edit modal, so labels and colors stay in sync everywhere.
export const CRITICALITY_OPTIONS = [
  { value: 'low', label: 'Low', description: 'Minimal impact if this application has issues or goes down.', level: 1, barColor: 'fill-gray-400 dark:fill-gray-500' },
  { value: 'medium', label: 'Medium', description: 'Noticeable impact on operations, but not business-critical.', level: 2, barColor: 'fill-amber-500' },
  { value: 'high', label: 'High', description: 'Critical to business operations; downtime causes significant impact.', level: 3, barColor: 'fill-red-500' },
]

// The application's own lifecycle phase — mirrors
// OrgComponentApplication's status enum (app/models/org_component_application.rb).
// A LeanIX-style factsheet lifecycle, matching this feature's overall design.
export const STATUS_OPTIONS = [
  { value: 'plan', label: 'Plan', description: 'Being evaluated or planned; not yet in use.' },
  { value: 'phase_in', label: 'Phase In', description: 'Being rolled out or onboarded.' },
  { value: 'active', label: 'Active', description: 'In active use.' },
  { value: 'phase_out', label: 'Phase Out', description: 'Being sunset; still running but scheduled for retirement.' },
  { value: 'end_of_life', label: 'End of Life', description: 'Fully retired or decommissioned.' },
]

// Each option pairs a backend sortable column with a direction. Must stay in
// sync with OrgApplicationsController::SORTABLE_COLUMNS.
export const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A to Z)', by: 'name', order: 'asc' },
  { value: 'name-desc', label: 'Name (Z to A)', by: 'name', order: 'desc' },
  { value: 'created_at-desc', label: 'Newest first', by: 'created_at', order: 'desc' },
  { value: 'created_at-asc', label: 'Oldest first', by: 'created_at', order: 'asc' },
  { value: 'criticality-desc', label: 'Criticality (High to Low)', by: 'criticality', order: 'desc' },
  { value: 'criticality-asc', label: 'Criticality (Low to High)', by: 'criticality', order: 'asc' },
]

const CRITICALITY_COLORS = {
  low: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
  medium: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  high: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
}

// Left border accent used on table rows / cards to make criticality scannable at a glance.
const CRITICALITY_BORDER_COLORS = {
  low: 'border-l-gray-300 dark:border-l-gray-600',
  medium: 'border-l-amber-400 dark:border-l-amber-500',
  high: 'border-l-red-500 dark:border-l-red-500',
}

const STATUS_COLORS = {
  plan: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400',
  phase_in: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  active: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  phase_out: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
  end_of_life: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
}

// A solid single-color dot (not the two-tone badge above) for the compact
// StatusField trigger/options, mirroring CriticalityBars' role for criticality.
const STATUS_DOT_COLORS = {
  plan: 'bg-gray-400 dark:bg-gray-500',
  phase_in: 'bg-blue-500',
  active: 'bg-green-500',
  phase_out: 'bg-amber-500',
  end_of_life: 'bg-red-500',
}

export function criticalityLabel(value) {
  return CRITICALITY_OPTIONS.find((o) => o.value === value)?.label || value
}

export function criticalityBadgeClass(value) {
  return CRITICALITY_COLORS[value] || CRITICALITY_COLORS.medium
}

export function criticalityBorderClass(value) {
  return CRITICALITY_BORDER_COLORS[value] || CRITICALITY_BORDER_COLORS.medium
}

export function statusLabel(value) {
  return STATUS_OPTIONS.find((o) => o.value === value)?.label || value
}

export function statusBadgeClass(value) {
  return STATUS_COLORS[value] || STATUS_COLORS.active
}

export function statusDotClass(value) {
  return STATUS_DOT_COLORS[value] || STATUS_DOT_COLORS.active
}
